/**
 * 客户物料指定供应商 - API工厂函数
 * 
 * @module api/customer-material-supplier
 * @backend backend/app/Modules/Sales/Controllers/CustomerMaterialSupplierController.php
 * @routes backend/routes/api.php - /api/v1/customer-material-suppliers
 */

import type {
  CustomerMaterialSupplier,
  CreateCustomerMaterialSupplierRequest,
  UpdateCustomerMaterialSupplierRequest,
  CustomerMaterialSupplierQueryParams,
  CheckDesignatedSupplierRequest,
  CheckDesignatedSupplierResponse,
  BatchToggleStatusRequest,
  BatchToggleStatusResponse,
} from '../../types/customer-material-supplier';

import type { PaginationApiResponse, ApiResponse } from '../../types/api';
import { API_ROUTES } from '../routes';

/**
 * HTTP 请求适配器接口
 */
export interface HttpAdapter {
  get<T = unknown>(url: string, config?: unknown): Promise<T>;
  post<T = unknown>(url: string, data?: unknown, config?: unknown): Promise<T>;
  put<T = unknown>(url: string, data?: unknown, config?: unknown): Promise<T>;
  delete<T = unknown>(url: string, config?: unknown): Promise<T>;
}

/**
 * 转换查询参数为API格式（camelCase -> snake_case）
 */
function toApiParams(params: CustomerMaterialSupplierQueryParams): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (params.customerId !== undefined) result.customer_id = params.customerId;
  if (params.materialId !== undefined) result.material_id = params.materialId;
  if (params.supplierId !== undefined) result.supplier_id = params.supplierId;
  if (params.designationType !== undefined) result.designation_type = params.designationType;
  if (params.status !== undefined) result.status = params.status;
  if (params.keyword !== undefined) result.keyword = params.keyword;
  if (params.onlyActive !== undefined) result.only_active = params.onlyActive;
  if (params.page !== undefined) result.page = params.page;
  if (params.perPage !== undefined) result.per_page = params.perPage;
  if (params.sort_by !== undefined) result.sort_by = params.sort_by;
  if (params.sort_order !== undefined) result.sort_order = params.sort_order;

  return result;
}

/**
 * 转换请求数据为API格式（camelCase -> snake_case）
 */
function toApiRequest(
  data: CreateCustomerMaterialSupplierRequest | UpdateCustomerMaterialSupplierRequest
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if ('customerId' in data && data.customerId !== undefined) result.customer_id = data.customerId;
  if ('materialId' in data && data.materialId !== undefined) result.material_id = data.materialId;
  if ('supplierId' in data && data.supplierId !== undefined) result.supplier_id = data.supplierId;
  if ('effectiveDate' in data) result.effective_date = data.effectiveDate;
  if ('expiryDate' in data) result.expiry_date = data.expiryDate;
  if ('designationType' in data && data.designationType !== undefined)
    result.designation_type = data.designationType;
  if ('priority' in data && data.priority !== undefined) result.priority = data.priority;
  if ('remark' in data) result.remark = data.remark;
  if ('status' in data && data.status !== undefined) result.status = data.status;

  return result;
}

/**
 * 创建客户物料指定供应商API
 * @param http HTTP客户端实例
 */
export function createCustomerMaterialSupplierApi(http: HttpAdapter) {
  return {
    /**
     * 获取分页列表
     */
    getList(params?: CustomerMaterialSupplierQueryParams) {
      return http.get<PaginationApiResponse<CustomerMaterialSupplier>>(
        API_ROUTES.customerMaterialSupplier.list,
        { params: toApiParams(params || {}) }
      );
    },

    /**
     * 获取详情
     */
    getDetail(id: number) {
      return http.get<ApiResponse<CustomerMaterialSupplier>>(
        API_ROUTES.customerMaterialSupplier.detail(id)
      );
    },

    /**
     * 创建
     */
    create(data: CreateCustomerMaterialSupplierRequest) {
      return http.post<ApiResponse<CustomerMaterialSupplier>>(
        API_ROUTES.customerMaterialSupplier.create,
        toApiRequest(data)
      );
    },

    /**
     * 更新
     */
    update(id: number, data: UpdateCustomerMaterialSupplierRequest) {
      return http.put<ApiResponse<CustomerMaterialSupplier>>(
        API_ROUTES.customerMaterialSupplier.detail(id),
        toApiRequest(data)
      );
    },

    /**
     * 删除
     */
    delete(id: number) {
      return http.delete<ApiResponse<null>>(API_ROUTES.customerMaterialSupplier.delete(id));
    },

    /**
     * 批量切换状态
     */
    batchToggleStatus(data: BatchToggleStatusRequest) {
      return http.post<ApiResponse<BatchToggleStatusResponse>>(
        API_ROUTES.customerMaterialSupplier.batchToggleStatus,
        { ids: data.ids, status: data.status }
      );
    },

    /**
     * 获取客户的所有指定供应商关系
     */
    getByCustomer(customerId: number, onlyActive = true) {
      return http.get<ApiResponse<CustomerMaterialSupplier[]>>(
        API_ROUTES.customerMaterialSupplier.byCustomer(customerId),
        { params: { only_active: onlyActive } }
      );
    },

    /**
     * 在客户下创建指定供应商关系
     */
    createForCustomer(customerId: number, data: CreateCustomerMaterialSupplierRequest) {
      return http.post<ApiResponse<CustomerMaterialSupplier>>(
        API_ROUTES.customerMaterialSupplier.byCustomer(customerId),
        toApiRequest(data)
      );
    },

    /**
     * 获取供应商被哪些客户指定
     */
    getBySupplier(supplierId: number, onlyActive = true) {
      return http.get<ApiResponse<CustomerMaterialSupplier[]>>(
        API_ROUTES.customerMaterialSupplier.bySupplier(supplierId),
        { params: { only_active: onlyActive } }
      );
    },

    /**
     * 校验采购是否符合客户指定要求
     */
    checkDesignatedSupplier(data: CheckDesignatedSupplierRequest) {
      return http.post<ApiResponse<CheckDesignatedSupplierResponse>>(
        API_ROUTES.purchase.checkDesignatedSupplier,
        {
          customer_id: data.customerId,
          items: data.items.map((item) => ({
            material_id: item.materialId,
            supplier_id: item.supplierId,
          })),
        }
      );
    },
  };
}

/**
 * API类型导出
 */
export type CustomerMaterialSupplierApi = ReturnType<typeof createCustomerMaterialSupplierApi>;
